import { useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import "../Landing.css";
import { CommonSections, TrustStrip } from "./_shared";

/**
 * Modèle "ROI Calculator" — 2026 design focused on conversion & ROI ahead of compliant DPE.
 * Hero = form → immediate DPE feedback → CTA “Simuler mes travaux” (tout de suite l’affinité investisseur)
 * Same Liquid glass tokens / styling as HomeReglette — drop-in compatible.
 */

type Class = {
  c: string;
  color: string;
  min: number;
  max: number;
  emissions: number;
};

const CLASS_DATA: Class[] = [
  { c: "A", color: "#34c759", min: 250, max: 450, emissions: 29 },
  { c: "B", color: "#86d94a", min: 451, max: 750, emissions: 61 },
  { c: "C", color: "#cdd836", min: 751, max: 1_100, emissions: 120 },
  { c: "D", color: "#ffd60a", min: 1_101, max: 1_650, emissions: 188 },
  { c: "E", color: "#ff9f0a", min: 1_651, max: 2_200, emissions: 256 },
  { c: "F", color: "#ff6a2c", min: 2_201, max: 3_000, emissions: 337 },
  { c: "G", color: "#ff3b30", min: 3_001, max: 5_000, emissions: 421 },
] as const;

const eur = (n: number) => `${Math.round(n).toLocaleString("fr-FR")} €`;

function ClassButton({
  cl,
  active,
  onClick,
  arialabel,
  activeClass,
  ariaCheched,
}: {
  cl: Class;
  active: number;
  onClick: () => void;
  arialabel: string;
  activeClass: number;
  ariaCheched: "true" | "false";
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-label={arialabel}
      aria-checked={ariaCheched}
      tabIndex={active === activeClass ? 0 : -1}
      className={`home-seg${active === activeClass ? " is-active" : ""}`}
      onClick={onClick}
    >
      <span className="bar" style={{ background: cl.color }} />
      <span className="cls">{cl.c}</span>
    </button>
  );
}

export default function HomeROI() {
  const [surface, setSurface] = useState<string>("90");
  const [dept, setDept] = useState<string>("75");
  const [sel, setSel] = useState<number>(3); // D par défaut
  const cur = CLASS_DATA[sel];
  const avg = Math.round((cur.min + cur.max) / 2);
  const factureYear = avg;
  const score = Math.round(((421 - cur.emissions) / 421) * 100);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(CLASS_DATA.length - 1, s + 1));
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(0, s - 1));
    }
  };

  return (
    <Layout>
      <Seo
        title="Diagnostic énergétique : vos travaux sont-ils rentables ?"
        description="Estimez en 2 minutes votre future étiquette énergie et le retour sur investissement de vos travaux d'isolation, pompe à chaleur ou solaire. Simulation gratuite, sans valeur légale."
        path="/"
      />
      <div className="home">
        {/* HERO */}
        <section className="home-wrap home-hero">
          <div className="home-glass home-hero-grid">
            <div>
              <span className="home-eyebrow">Diagnostic de Performance Énergétique</span>
              <h1 className="home-h1">
                Votre écotravaux sont-ils rentables ?
                <span className="home-accent">pour 22€/mois en moyenne ?</span>
              </h1>
              <p className="home-sub">
                Estimez en 2 minutes votre future étiquette énergie et le retour sur
                investissement de vos travaux d’isolation, pompe à chaleur, ou solaire.
              </p>

              <form className="home-fast-form" onSubmit={(e) => e.preventDefault()}>
                <div className="home-grid-simple">
                  <label>
                    <span>Surface (m²)</span>
                    <input
                      type="number"
                      value={surface}
                      onChange={(e) => setSurface(e.target.value.replace(/\D+/, ""))}
                      min={10}
                      max={500}
                    />
                  </label>
                  <label>
                    <span>Département (75, 69…)</span>
                    <select value={dept} onChange={(e) => setDept(e.target.value)} className="rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                      <option value="75">Paris (75)</option>
                      <option value="69">Lyon (69)</option>
                      <option value="33">Bordeaux (33)</option>
                    </select>
                  </label>
                </div>
                <button className="home-btn home-btn-primary home-btn-block">
                  Calculer mon DPE gratuitement
                </button>
              </form>
              <p className="home-privacy">Sans valeur légale — 1ᵉʳ logement gratuit</p>
            </div>

            {/* SECONDARY PANEL → ROI ON THE SIDE */}
            <div className="home-glass home-roi-panel">
              <div className="home-roi-tiles">
                <div className="tile tile-red">
                  <h4>Avant travaux</h4>
                  <div className="tile-classe" style={{ color: CLASS_DATA[5].color }}>
                    {CLASS_DATA[5].c}-surdimensionné
                  </div>
                  <div className="tile-value pyramid">{eur(3_250)}</div>
                  <div className="tile-fonte">↯ Consommation actuelle</div>
                </div>

                <div className="tile tile-green">
                  <h4>Après travaux</h4>
                  <div className="">🎯 Objectif étiquette</div>
                  <div className="tile-value big">{eur(1_120)}</div>
                  <ul className="tile-aides-inside">
                    <li className="reuse-check">≈ MaPrimeRéno’ 7 500 €</li>
                    <li className="reuse-check">CEE 3 240 € → 9% supplémentaire</li>
                    <li className="reuse-check">Durée du chantier: 3 mois</li>
                  </ul>
                </div>
              </div>

              {/* ROI METRIC */}
              <div className="home-roi-metric">
                <div>
                  <span className="label">Économies annuelles</span>
                  <span className="value">2 130 €/an</span>
                </div>
                <div>
                  <span className="label">Récup’ aides</span>
                  <span className="value">10 800 € <small>(56 % du coût)</small></span>
                </div>
                <div>
                  <span className="label">Coût net restant</span>
                  <span className="value financier">5 120 €</span>
                </div>
                <div>
                  <span className="label italic">→ Retour sur investissement en 2 ans 4 mois</span>
                </div>
              </div>

              <div className="home-cta-row small-gap">
                <Link
                  to="/logements/new"
                  className="home-btn home-btn-primary home-btn-block"
                  replace
                >
                  ⚡ Je simule mes travaux gratuit
                </Link>
                <button className="home-btn home-btn-ghost outline-home">
                  Terminé — besoin d’un audit
                </button>
                <p className="tiny note">
                  Estimation indicative basée sur une rénovation 100% énergie verte —
                  adaptée techniquement par votre ville et revenus.
                </p>
            </div>
          </div>
        </div>

        </section>

        {/* SECONDARY ROI SPECTRUM → SITUATE & driving CTA */}
        <section className="home-wrap home-section">
          <div className="home-section-head"><span className="home-eyebrow">Prévisualisez votre rentabilité</span></div>
          <div className="home-glass home-reglette home-reglette-large">
            <div className="home-reglette-cap">
              Pour un logement type de {surface} m² {dept} — touchez une classe :
            </div>
            <div
              className="home-spectrum"
              role="radiogroup"
              aria-label="Choisir une classe énergie pour obtenir une estimation ROI"
              onKeyDown={onKey}
            >
              {CLASS_DATA.map((cl, i) => (
                <ClassButton
                  key={cl.c}
                  cl={cl}
                  active={sel}
                  onClick={() => setSel(i)}
                  arialabel={`Classe ${cl.c}`}
                  activeClass={sel}
                  ariaCheched={i === sel ? "true" : "false"}
                />
              ))}
            </div>
            <div className="home-facture">
              <span className="big">≈ {eur(factureYear)} / an</span>
              <span className="range">fourchette {eur(cur.min)} – {eur(cur.max)}</span>
            </div>
            <ul className="home-profils">
              <li>
                <div className="k">Classe {cur.c}</div>
                <div className="v">{score} points améliorés 🏆</div>
              </li>
              <li>
                <div className="k">CO₂ évité/an</div>
                <div className="v">{Math.round(cur.emissions * 0.14)} kg</div>
              </li>
            </ul>
            <p className="home-reglette-note">
              Chiffres indicatifs basés sur la méthode officielle 3CL-DPE 2021. Votre
              projet réel dépend de l’état du logement et de votre éligibilité aux aides.
            </p>
          </div>
        </section>

        <TrustStrip />
        <CommonSections />
      </div>
    </Layout>
  );
}
